import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles, CircularProgress } from '@material-ui/core';
import Forms from 'components/Forms';
import Charts from 'components/Charts';
import Store from 'store/store';
import Router from 'components/Router/Router';
import {
  PolicyRiskScoreNamed,
  PastPerformanceAggregatesType
} from 'store/modules/Charts/types';
import { BoxWrapper, Button } from 'components/Base';
import { RiskGroupType } from 'store/modules/Firm/types';
import BottomNavBar from 'components/Cases/BottomNavBar';
import Icons from 'components/Icons';
import classNames from 'classnames';
import PastPerformance from 'components/IPS/PastPerformance';
import PastPerformanceTable from 'components/IPS/PastPerformanceTable/PastPerformanceTable';
import api from 'api';
import ScatterChart from 'components/InvestmentAnalyser/ScatterChart';
import RiskDescriptionModal from 'components/RiskDescription/RiskDescriptionModal';
import ChargesWrapper from 'components/Policies/Charges/ChargesWrapper';
import { combinePolicyRiskScores } from 'components/Policies/utils';
import BreakDownWrapper from 'components/Charts/Utils/Breakdown/BreakDownWrapper';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import { getComfortRanges } from 'components/Charts/Utils/MorningstarRiskProfiler/utils';
import { comfortRangeConfig } from 'components/RiskMapping/utils';

const { useHistory, useParams } = Router;
const { connect, selectors, actions } = Store;

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    marginBottom: '1rem'
  },
  box: {
    height: '100%',
    boxShadow: 'none'
  },
  gridBox: {
    minHeight: 433,
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  pastPerformanceBox: {
    minHeight: '25.125rem'
  },
  container: {
    paddingBottom: '86px'
  },
  chartBox: {
    height: '100%',
    padding: '2rem'
  },
  tooltip: {
    margin: 0
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flex: 1
  }
});

interface RecommendationProps {
  initialValues: any;
  portfolioRiskScore: Array<PolicyRiskScoreNamed>;
  pastPerformanceAggregates: PastPerformanceAggregatesType;
  hasExistingPolicies: boolean;
  firmRiskGroup: RiskGroupType;
  isLoadingPastPerformanceData: any;
  existingPoliciesRiskScore: any;
  recommendedPoliciesRiskScore: any;
  totalPerformanceData: any;
  updatePerformancePeriod: Function;
  caseInfo: any;
  recommendedRiskAnalysis: any;
  setPastPerformance: Function;
}
const Recommendations = ({
  initialValues,
  portfolioRiskScore,
  pastPerformanceAggregates,
  hasExistingPolicies,
  firmRiskGroup,
  isLoadingPastPerformanceData,
  totalPerformanceData,
  existingPoliciesRiskScore,
  recommendedPoliciesRiskScore,
  updatePerformancePeriod,
  caseInfo,
  recommendedRiskAnalysis,
  setPastPerformance
}: RecommendationProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { portfolio } = useParams() as any;
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [comfortRanges, setComfortRanges] = useState([] as any);

  useEffect(() => {
    const ranges = getComfortRanges(caseInfo.riskProfiler, comfortRangeConfig, [
      recommendedRiskAnalysis['comfort_ranges']
    ]);
    setComfortRanges(ranges);
  }, [caseInfo.riskProfiler, comfortRangeConfig]);

  useEffect(() => {
    updatePerformancePeriod();
    // eslint-disable-next-line
  }, []);

  const isPastPerformanceData =
    initialValues.recommendedPolicies.length > 0 ||
    initialValues.policies.length > 0;
  const recommendedRiskScore = portfolioRiskScore.filter(
    (policy: any) => policy.name === 'Recommended'
  )[0]?.risk_score;

  const modifiedData = combinePolicyRiskScores(
    existingPoliciesRiskScore,
    recommendedPoliciesRiskScore
  );

  return (
    <Grid container className={classes.container}>
      <Forms.Recommendations initialValues={initialValues} />
      {!isPastPerformanceData ? (
        <div
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
            padding: '2rem',
            width: '100%'
          }}
        >
          <CreateNewFolderIcon
            htmlColor="#6b7280"
            style={{ width: '36px', height: '36px' }}
          />
          <div style={{ fontWeight: 600 }}>No Policies</div>
          <div
            style={{
              color: '#6b7280',
              maxWidth: '400px',
              margin: '0 auto'
            }}
          >
            Get started by adding an existing or recommended policy
          </div>
          <div
            style={{
              padding: '1rem 0rem 0rem'
            }}
          >
            <Button
              onClick={() => history.push(`/${portfolio}/existing-policies`)}
            >
              Add Existing Policy
            </Button>
          </div>
        </div>
      ) : (
        <Grid container direction="row" className={classes.root} spacing={2}>
          {caseInfo.riskProfiler !== 'EValue' && !!comfortRanges ? (
            <Grid item xs={12} md={12} style={{ minHeight: '27rem' }}>
              <BoxWrapper className={classes.box} title="Risk Analysis">
                <div style={{ padding: '0 5rem' }}>
                  <Charts.MorningstarRiskProfiler.PolicyAnalysis
                    ranges={comfortRanges}
                    markers={[
                      {
                        position: 45,
                        type: 'dotted',
                        label: 'Current Position'
                      },
                      { position: 75, type: 'solid', label: 'High Threshold' }
                    ]}
                  />
                </div>
              </BoxWrapper>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper
                  title="Risk"
                  className={classes.gridBox}
                  headerChildren={
                    recommendedRiskScore ? (
                      <Button
                        type="button"
                        onClick={() => setIsRiskModalOpen(true)}
                      >
                        View Risk
                      </Button>
                    ) : (
                      <></>
                    )
                  }
                >
                  <div
                    style={{
                      flex: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: '1rem'
                    }}
                  >
                    <Charts.Recommendation.Risk
                      data={portfolioRiskScore}
                      hasExistingPolicies={hasExistingPolicies}
                      riskGroup={firmRiskGroup}
                    />
                  </div>
                </BoxWrapper>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BoxWrapper
                  title="Policy risk score"
                  className={classes.gridBox}
                >
                  <Charts.ExistingPolicyAnalysis.PoliciesRiskScore
                    data={modifiedData}
                    riskGroup={firmRiskGroup}
                  />
                </BoxWrapper>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <ChargesWrapper />
          </Grid>

          <Grid item xs={12}>
            <BoxWrapper
              className={classNames(
                classes.gridBox,
                classes.pastPerformanceBox
              )}
              title="Performance"
              info={pastPerformanceAggregates.date_range}
              footer="Where regular contributions are being recommended, past performance is  calculated based on the annual sum of contributions payable."
              helperText={
                <div>
                  Performance is based on the current fund allocation and
                  doesn't take into account any previous fund switches
                </div>
              }
            >
              {isLoadingPastPerformanceData ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <Grid item xs={12} style={{ height: '100%' }}>
                  <PastPerformance
                    data={pastPerformanceAggregates.data}
                    errors={pastPerformanceAggregates.failed_codes}
                    limits={pastPerformanceAggregates.limits}
                    earliestDates={pastPerformanceAggregates.earliest_dates}
                    footprints={pastPerformanceAggregates.footprints}
                    isAnyPolicyAdded={isPastPerformanceData}
                    getPastPerformanceData={api.getPastPerformanceAggregateData}
                    setData={(values: any) => {
                      setPastPerformance({
                        ...pastPerformanceAggregates,
                        ...values
                      });
                    }}
                  />

                  {pastPerformanceAggregates.tabular_data &&
                    Object.keys(pastPerformanceAggregates.tabular_data).length >
                      0 && (
                      <div style={{ margin: '1rem 1rem 2rem 1rem' }}>
                        <PastPerformanceTable
                          data={pastPerformanceAggregates.tabular_data}
                        />
                      </div>
                    )}
                </Grid>
              )}
            </BoxWrapper>
            {recommendedRiskScore ? (
              <RiskDescriptionModal
                score={recommendedRiskScore}
                isOpen={isRiskModalOpen}
                setIsOpen={setIsRiskModalOpen}
              />
            ) : (
              <></>
            )}
          </Grid>

          <Grid item xs={12}>
            <BoxWrapper className={classes.gridBox} title="Scatter Chart">
              {isLoadingPastPerformanceData ||
              (pastPerformanceAggregates.tabular_data &&
                !Object.keys(pastPerformanceAggregates.tabular_data).length) ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <Grid item xs={12} className={classes.chartBox}>
                  <ScatterChart
                    data={totalPerformanceData}
                    performanceData={pastPerformanceAggregates.tabular_data}
                  />
                </Grid>
              )}
            </BoxWrapper>
          </Grid>

          <Grid item xs={12}>
            <BreakDownWrapper />
          </Grid>
          <BottomNavBar
            rightButtons={[
              <Button
                endIcon={<Icons.RightArrow />}
                key="Adviser charges button"
                onClick={() => history.push(`/${portfolio}/advisor-charges`)}
                type="button"
              >
                Adviser charges
              </Button>
            ]}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default connect(
  (state: any) => ({
    portfolioRiskScore: selectors.caseSelectors.portfolioRiskScore(state),
    pastPerformanceAggregates:
      selectors.caseSelectors.pastPerformanceAggregates(state),
    totalPerformanceData: selectors.caseSelectors.totalPerformanceData(state),
    hasExistingPolicies: selectors.caseSelectors.hasExistingPolicies(state),
    firmRiskGroup: selectors.firmSelectors.riskGroup(state),
    existingPoliciesRiskScore:
      selectors.caseSelectors.existingPoliciesRiskScore(state),
    recommendedPoliciesRiskScore:
      selectors.caseSelectors.recommendedPoliciesRiskScore(state),
    caseInfo: selectors.caseSelectors.case(state),
    recommendedRiskAnalysis:
      selectors.caseSelectors.recommendedRiskAnalysis(state)
  }),
  {
    updatePerformancePeriod:
      actions.investmentAnalyserActions.updatePerformancePeriod,
    setPastPerformance: actions.caseActions.setPastPerformanceAggregates
  }
)(Recommendations);
